/**
 * Creating a customer end program.
 *
 * Programs are the unit of value in this funnel — a design win is worth the
 * customer's programme, not the purchase order — and until now the application
 * could read, edit and cancel one but never create one. `POST /api/programs`
 * is registered through the resource factory with the full verb set and had no
 * caller anywhere in `apps/web/src`, so a hand-typed opportunity could not
 * satisfy `QUALIFIED`'s `program_id` and stuck at Prospect for good.
 *
 * **Two fields, not nine.** `createProgramSchema` requires `account_id` and
 * `name` and makes everything else optional; the table defaults `end_market`
 * to `other` and `status` to `concept`, so a short form is a valid create. The
 * one optional field offered is `end_market`, because it is the dimension the
 * account page and the analytics both group programs by, and a list of
 * programs all reading "Other" is a list that answers nothing. `status`
 * is not offered: `concept` is right for a programme being recorded for the
 * first time, and the program page edits it afterwards.
 *
 * **`export_sensitive` is not offered either, and that is the point** (§8 item
 * 6): it is set by a human and never derived, so a create form that guessed it
 * from the account or the end market would be doing exactly the forbidden
 * thing. `CreateAccountDialog` omits it for the same reason and the compliance
 * surface is where it is set.
 *
 * **No permission gate.** `permissions.ts` has `program.create: ALL`, and the
 * write is scoped per-instance to the account (the account's owner, any
 * Manager, any Admin) by `ANCESTRY`. Cancelling a program is `Manager`/`Admin`
 * only under §2 rule 3 because it closes opportunities across accounts;
 * creating one closes nothing, so that restriction does not carry across. The
 * server decides, as §2 rule 1 says it must.
 */

import { useState } from 'react';
import { END_MARKETS } from '@menlo/shared';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/label';
import { useApiMutation } from '@/lib/queries';
import { humanize } from '@/lib/format';

export interface CreatedProgram {
  id: string;
  name: string;
}

export function CreateProgramDialog({
  accountId,
  initialName = '',
  onClose,
  onCreated,
}: {
  accountId: string;
  initialName?: string;
  onClose: () => void;
  onCreated: (program: CreatedProgram) => void;
}): React.ReactElement {
  const [name, setName] = useState(initialName);
  const [endMarket, setEndMarket] = useState<string>('other');

  const create = useApiMutation<{ program: CreatedProgram }, void>(
    () => ({
      path: '/api/programs',
      method: 'POST',
      body: { account_id: accountId, name: name.trim(), end_market: endMarket },
    }),
    {
      // Prefix-matched, so both `['programs', <account>]` keys — the account
      // page's list and the stage gate's picker — refetch without either
      // screen knowing about the other.
      invalidate: [['programs']],
      onSuccess: (data) => onCreated(data.program),
    },
  );

  return (
    <Dialog
      open
      onClose={onClose}
      size="sm"
      title="New program"
      description="The customer's end programme this deal designs into. Everything else is editable on the program page."
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => create.mutate()}
            disabled={name.trim() === '' || create.isPending}
          >
            Create program
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name">
          <Input value={name} onChange={(event) => setName(event.target.value)} autoFocus />
        </Field>
        <Field label="End market">
          <Select value={endMarket} onChange={(event) => setEndMarket(event.target.value)}>
            {END_MARKETS.map((value) => (
              <option key={value} value={value}>
                {humanize(value)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </Dialog>
  );
}
